import { observer } from 'mobx-react-lite';
import React from 'react';
import { MessageAttachments } from '../stores/appStateStore';
import { runInAction } from 'mobx';
import { AppProps } from './App';
import { dfsFindAllAttachments, dfsMimeContentType, encodeStringIconvLite, formatBytes, formatUnixTimestamp } from '../lib/utils';
import { decodeStringIconvLite } from '../lib/utils';
import { parseMimeTree } from '../gflib/src/lib/mime-tree/parseMimeTree';
import { LeafMimeNode, MimeNode } from '../gflib/src/lib/mime-tree/types';
import { mhtmlToHtmlNoPronom } from '../lib/mimeHtml';
import cleanHtml from '../lib/dom-purify';
import { decodeTransferEnc } from '../gflib/src/lib/mime-tree/decodeTransferEnc';

interface MessageListItemProps extends AppProps {
    messageIx: number;
}

function replaceAttachmentPointersWithInlineB64(html: string, attachments: MessageAttachments) {
    const parser = new DOMParser();
    const document = parser.parseFromString(html, 'text/html');

    // ^= is prefix notation, we are searching for images whose src attribute begins with "cid:"
    const imageRefs = document.querySelectorAll('img[src^="cid:"]');
    if(imageRefs) {
        imageRefs.forEach(e => {
            const cid = e.getAttribute('src')?.substring(4);
            if(cid) {
                e.setAttribute('class', 'height-500px');
                const ix = attachments.cidMap.get(cid);
                if(ix !== undefined) {
                    const attachNode = attachments.nodeList[ix];
                    const b64 = (attachNode as LeafMimeNode).rawBody;
                    const dataType = attachNode.contentType;
                    const transferEncoding = attachNode.transferEncoding;
                    e.setAttribute('src', `data:${dataType};${transferEncoding},${b64}`);
                }
            }
        });
    }

    const serializer = new XMLSerializer();
    const updatedHtml = serializer.serializeToString(document);
    
    return updatedHtml;
} // replaceAttachmentPointersWithInlineB64()

const MessageListItem = observer((props: MessageListItemProps) => {
    const {state, messageIx} = props;
    const fileIx = state.messageQueryAndIxList.fileIx;
    const currentMessage = state.mboxList[fileIx]?.messageData[messageIx];
    let dispSubject: string|undefined;
    let dispFrom: string|undefined; 
    let dispTo: string|undefined; 
    let dispDate: string|undefined;
    let dispWarning = false;
    const dispSize = formatBytes(currentMessage.end - currentMessage.start);

    if(currentMessage.headers) {
        const headers = currentMessage.headers;
        dispSubject = headers.subject? headers.subject : '';
        dispFrom = headers.from? headers.from : '';
        dispTo = headers.to? headers.to : '';
        dispDate = headers.date!==undefined? formatUnixTimestamp(headers.date) : '';
        if(dispSubject === '') {
            dispSubject  = currentMessage.messageFirstTwenty? currentMessage.messageFirstTwenty+'...':'';
        }
        if(dispFrom === '') {
            dispFrom = currentMessage.fromLine? currentMessage.fromLine : '';
        }
        if(headers.warning) {
            dispWarning = true;
        }
    } else {
        dispSubject  = currentMessage.messageFirstTwenty? currentMessage.messageFirstTwenty+'...':'';
        dispFrom = currentMessage.fromLine? currentMessage.fromLine : '';
        dispTo = '';
        dispDate = '';
    }

    const openMessage = async () => {
        const messageBuf = await state.mboxList[fileIx].reader.readBytes(currentMessage.start, currentMessage.end);
        let currentMessageMime: MimeNode|undefined;
        runInAction(() => {
            state.loadedMore = false;
            try{
                currentMessageMime = parseMimeTree(messageBuf, decodeStringIconvLite);
            }
            catch {;}
        });
        if(currentMessageMime) {
            const attachments: MessageAttachments = {
                nodeList: dfsFindAllAttachments(currentMessageMime),
                cidMap: new Map<string, number>()
            };
            runInAction(() => {
                state.currentMessage.attachments = attachments;
            });
            const {nodeList, cidMap} = state.currentMessage.attachments;
            for(let i = 0; i < nodeList.length; i++) {
                const rawCid = nodeList[i].contentId;
                const properCid = rawCid?.substring(1, rawCid.length-1);
                if(properCid) {
                    cidMap.set(properCid, i);
                }
            }

            runInAction(() => {
                state.currentMessage.headers = currentMessageMime!.headers;
            });
            const htmlMimeNode = dfsMimeContentType(currentMessageMime, 'text/html');
            if(htmlMimeNode) {
                const messageHtml = cleanHtml((await mhtmlToHtmlNoPronom(htmlMimeNode, {
                    normalizeToUtf8: false,
                    decode: decodeStringIconvLite,
                    encode: encodeStringIconvLite,
                })).html);
                runInAction(() => {
                    state.currentMessage.html = messageHtml;
                });
                const messageRenderHtml = replaceAttachmentPointersWithInlineB64(messageHtml, state.currentMessage.attachments);
                runInAction(() => {
                    state.currentMessage.renderHtml = messageRenderHtml;
                });
            } else { // no html found
                runInAction(() => {
                    state.currentMessage.html = '';
                });
            }

            const plaintextMimeNode = dfsMimeContentType(currentMessageMime, 'text/plain');
            if(plaintextMimeNode) {
                if(plaintextMimeNode.transferEncoding) {
                    runInAction(() => {
                        // Buffer as UTF8
                        state.currentMessage.plaintext = decodeTransferEnc((plaintextMimeNode as LeafMimeNode)).toString();
                    });
                } else {
                    runInAction(() => {
                        state.currentMessage.plaintext = (plaintextMimeNode as LeafMimeNode).rawBody;
                    });
                }
            } else { // no plaintext found
                runInAction(() => {
                    state.currentMessage.plaintext = '';
                });
            }
        } else { // not mime email, just render plaintext
            runInAction(() => {
                state.currentMessage.html = '';
                state.currentMessage.headers = undefined;
                state.currentMessage.plaintext = messageBuf.toString('binary');
            });
        }
        runInAction(() => {
            // Curried display subject
            state.currentMessage.subject = dispSubject?dispSubject:'';
            state.currentMessage.mime = messageBuf.toString();
        });
    }; // openMessage()

    return ( // Table stuff
        <tr className="message-table-row" onClick={openMessage}>
            <td>{dispWarning?
                <div className="tooltip">⚠<span className="tooltiptext">Unexpected behavior detected by mbox viewer, possible issue with email headers display.</span></div>
                :''}{dispSubject}</td>
            <td>{dispFrom}</td>
            <td>{dispTo}</td>
            <td>{dispDate}</td>
            <td>{dispSize}</td>
        </tr>
    );
});


export default MessageListItem;